import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle, Users } from 'lucide-react';
import { getAnalyticsSessionId, trackEvent } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PETITION_NAME = 'secure-your-vote-2026';

const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const PetitionPage = () => {
  const [petition, setPetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', zip_code: '', phone: '', comments: '', optin: true
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/petition/view?petition_name=${PETITION_NAME}`)
      .then(res => { setPetition(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.zip_code) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await trackEvent('petition_submit', '/sign-the-petition', null, { metadata: { petition_name: PETITION_NAME } });
      await axios.post(`${API_URL}/api/petition/submit`, {
        petition_name: PETITION_NAME,
        ...form,
        email: form.email.toLowerCase(),
        session_id: getAnalyticsSessionId(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://securethevotemd.com/sign-the-petition/';

  return (
    <div className="min-h-screen bg-white" data-testid="petition-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero Banner */}
      <div className="relative pt-[50px]">
        <div className="absolute inset-0" style={{ backgroundColor: '#9E1B32' }} />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.85)' }} />
        <div className="relative z-10 py-16 sm:py-20">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Sign the Petition
            </motion.h1>
            <motion.hr
              className="w-16 mx-auto mt-6 border-t-2 border-[#F6BF58]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Petition Title + Stats */}
      <div className="text-center py-10 px-4 max-w-3xl mx-auto">
        <h2 className="text-[#9B1E37] text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          {petition?.title || 'Secure YOUR Vote!'}
        </h2>
        <p className="text-slate-500 text-lg mb-6">
          {petition?.description || 'Join us in petitioning the Maryland General Assembly.'}
        </p>
      </div>

      {/* Main Content */}
      <main className="pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* Left: Petition Info */}
            <div className="lg:col-span-3 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-[#9B1E37] mb-3">Why This Petition Matters</h2>
                <p className="text-slate-600 leading-relaxed">Maryland's elections must be transparent, secure, and trustworthy. We're calling on our state legislature and election officials to implement common-sense reforms that will restore confidence in our democratic process.</p>
              </div>

              {/* Petition Message - Scrollable */}
              {petition?.petition_message && (
                <div
                  className="rounded-lg bg-slate-50 p-5 max-h-[400px] overflow-y-auto"
                  style={{ scrollbarWidth: 'thin', borderLeft: '3px solid #9B1E37' }}
                  data-testid="petition-scroll"
                >
                  <div
                    className="prose prose-sm max-w-none prose-p:text-slate-600 prose-p:leading-relaxed prose-strong:text-slate-900"
                    dangerouslySetInnerHTML={{ __html: petition.petition_message }}
                  />
                </div>
              )}

              {/* What We're Fighting For */}
              <div>
                <h3 className="text-xl font-bold text-[#9B1E37] mb-3">What We're Fighting For:</h3>
                <ul className="space-y-2 text-slate-600">
                  <li><strong className="text-[#9B1E37]">Voter ID Requirements:</strong> Ensure every voter is who they claim to be</li>
                  <li><strong className="text-[#9B1E37]">Signature Verification:</strong> Validate mail-in ballots with proper authentication</li>
                  <li><strong className="text-[#9B1E37]">Transparent Vote Counting:</strong> Open the process to public observation</li>
                  <li><strong className="text-[#9B1E37]">Regular List Maintenance:</strong> Keep voter rolls accurate and up-to-date</li>
                  <li><strong className="text-[#9B1E37]">Paper Ballot Trail:</strong> Maintain verifiable records of every vote</li>
                  <li><strong className="text-[#9B1E37]">Board Accountability:</strong> Hold election officials responsible for compliance</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-[#9B1E37] mb-3">Your Voice Matters</h3>
                <p className="text-slate-600 leading-relaxed">Every signature sends a powerful message to our elected officials. Together, we can secure Maryland's elections and protect the integrity of our democracy.</p>
              </div>

              {/* Share Section */}
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Share This Petition</h3>
                <div className="flex gap-3">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded text-sm font-medium hover:opacity-90"
                  >
                    <i className="fab fa-facebook-f"></i> Facebook
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Join me in demanding election integrity in Maryland!')}&url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded text-sm font-medium hover:opacity-90"
                  >
                    <i className="fab fa-x-twitter"></i> Share
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent('Sign the Election Integrity Petition')}&body=${encodeURIComponent(`I just signed a petition for election integrity in Maryland. Join me: ${shareUrl}`)}`}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded text-sm font-medium hover:opacity-90"
                  >
                    <i className="fas fa-envelope"></i> Email
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Sign Form */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 bg-slate-50 rounded-xl p-6 border" data-testid="petition-form-card">
                {submitted ? (
                  <motion.div
                    className="text-center py-6"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid="petition-success"
                  >
                    <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      Your signature has been added. Share this petition to amplify your voice!
                    </p>
                    <div className="flex gap-2 justify-center">
                      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-[#1877F2] text-white rounded text-xs"><i className="fab fa-facebook-f"></i></a>
                      <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('I signed the petition for election integrity in Maryland!')}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-black text-white rounded text-xs"><i className="fab fa-x-twitter"></i></a>
                    </div>
                  </motion.div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-[#9B1E37] mb-4">Add Your Name</h2>

                    <form onSubmit={handleSubmit} className="space-y-3.5">
                      <div className="space-y-1">
                        <Label className="text-sm">First Name <span className="text-red-500">*</span></Label>
                        <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-10" data-testid="petition-first-name" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-sm">Last Name <span className="text-red-500">*</span></Label>
                        <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-10" data-testid="petition-last-name" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-sm">Email Address <span className="text-red-500">*</span></Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10" data-testid="petition-email" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-sm">ZIP Code <span className="text-red-500">*</span></Label>
                        <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} pattern="[0-9]{5}" maxLength={5} className="h-10" data-testid="petition-zip" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-sm">Phone Number <span className="text-slate-400">(optional)</span></Label>
                        <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10" data-testid="petition-phone" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-sm">Comments <span className="text-slate-400">(optional)</span></Label>
                        <Textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} rows={3} data-testid="petition-comments" />
                      </div>

                      <div className="flex items-start gap-2.5 pt-1">
                        <input
                          type="checkbox"
                          checked={form.optin}
                          onChange={(e) => setForm({ ...form, optin: e.target.checked })}
                          className="mt-0.5 h-4 w-4 accent-[#9B1E37]"
                          data-testid="petition-optin"
                        />
                        <span className="text-xs text-slate-500 leading-relaxed">
                          I agree to receive communications from Secure the Vote MD, including updates via email, newsletter, and phone.
                        </span>
                      </div>

                      {error && (
                        <p className="text-red-500 text-sm" data-testid="petition-error">{error}</p>
                      )}

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full h-12 text-base font-bold tracking-wider"
                        style={{ backgroundColor: '#F6BF58', color: '#1a1a1a' }}
                        data-testid="petition-submit"
                      >
                        {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Sign the Petition'}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default PetitionPage;
